import React from 'react';

// Base Skeleton Component
interface SkeletonProps {
  className?: string;
  width?: string | number;
  height?: string | number;
  rounded?: boolean;
  animate?: boolean;
}

const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  width = '100%',
  height = '1rem',
  rounded = false,
  animate = true
}) => {
  const baseClasses = `bg-secondary-200 ${animate ? 'animate-pulse' : ''} ${rounded ? 'rounded-full' : 'rounded'}`;
  const style = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  return <div className={`${baseClasses} ${className}`} style={style} />;
};

// Card Skeleton for Dashboard Stats — matches StatCard layout exactly
export const StatCardSkeleton: React.FC = () => (
  <div className="bg-secondary-50 p-4 rounded-xl animate-pulse border border-black/5">
    {/* Icon + percent change row */}
    <div className="flex items-start justify-between mb-3">
      <div className="w-9 h-9 rounded-lg bg-secondary-200" />
      <div className="w-12 h-5 rounded-lg bg-secondary-200" />
    </div>
    {/* Value */}
    <div className="h-7 w-20 rounded-md bg-secondary-300 mb-1.5" />
    {/* Title */}
    <div className="h-3.5 w-28 rounded bg-secondary-200" />
  </div>
);

// Table Skeleton
interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
}

export const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rows = 5,
  columns = 4,
  showHeader = true
}) => (
  <div className="bg-white rounded-xl shadow-sm border border-secondary-100 overflow-hidden">
    {showHeader && (
      <div className="px-6 py-4 border-b border-secondary-200 flex items-center justify-between bg-gradient-to-r from-secondary-50/50 to-white">
        <Skeleton width="35%" height={18} className="bg-secondary-300" />
        <Skeleton width={64} height={18} className="bg-secondary-200 rounded-lg" />
      </div>
    )}
    <div className="overflow-x-auto">
      <table className="min-w-full">
        {showHeader && (
          <thead className="bg-secondary-50 border-b-2 border-secondary-200">
            <tr>
              {Array.from({ length: columns }, (_, index) => (
                <th key={index} className="px-4 py-3">
                  <Skeleton width="75%" height={14} className="bg-secondary-300" />
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody className="divide-y divide-secondary-100">
          {Array.from({ length: rows }, (_, rowIndex) => (
            <tr key={rowIndex} className="animate-pulse">
              {Array.from({ length: columns }, (_, colIndex) => (
                <td key={colIndex} className="px-4 py-3.5">
                  <Skeleton
                    width={colIndex === 0 ? "85%" : colIndex === columns - 1 ? "50%" : "70%"}
                    height={14}
                    className="bg-secondary-200"
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Chart Skeleton
export const ChartSkeleton: React.FC<{ title?: string }> = ({ title }) => (
  <div className="bg-white rounded-lg shadow-sm p-6">
    <div className="flex items-center justify-between mb-4">
      {title ? (
        <h3 className="text-lg font-medium text-secondary-900">{title}</h3>
      ) : (
        <Skeleton width="40%" height={20} className="bg-secondary-300" />
      )}
      <Skeleton width={24} height={24} rounded className="bg-secondary-300" />
    </div>
    <div className="h-64 bg-secondary-100 rounded-lg animate-pulse flex items-center justify-center">
      <div className="flex space-x-2">
        <Skeleton width={8} height={120} className="bg-secondary-300" />
        <Skeleton width={8} height={100} className="bg-secondary-300" />
        <Skeleton width={8} height={140} className="bg-secondary-300" />
        <Skeleton width={8} height={80} className="bg-secondary-300" />
        <Skeleton width={8} height={160} className="bg-secondary-300" />
        <Skeleton width={8} height={110} className="bg-secondary-300" />
        <Skeleton width={8} height={130} className="bg-secondary-300" />
      </div>
    </div>
  </div>
);

// Form Skeleton
interface FormSkeletonProps {
  fields?: number;
  showTitle?: boolean;
}

export const FormSkeleton: React.FC<FormSkeletonProps> = ({
  fields = 6,
  showTitle = true
}) => (
  <div className="bg-white rounded-lg shadow-sm p-6">
    {showTitle && (
      <div className="mb-6">
        <Skeleton width="30%" height={28} className="bg-secondary-300 mb-2" />
        <Skeleton width="60%" height={16} className="bg-secondary-200" />
      </div>
    )}
    <div className="space-y-4">
      {Array.from({ length: fields }, (_, index) => (
        <div key={index} className="animate-pulse">
          <Skeleton width="25%" height={16} className="bg-secondary-300 mb-2" />
          <Skeleton width="100%" height={40} className="bg-secondary-200" />
        </div>
      ))}
    </div>
    <div className="mt-6 flex space-x-3">
      <Skeleton width={100} height={40} className="bg-secondary-300" />
      <Skeleton width={80} height={40} className="bg-secondary-200" />
    </div>
  </div>
);

// List Skeleton
interface ListSkeletonProps {
  items?: number;
  showAvatar?: boolean;
  lines?: number;
}

export const ListSkeleton: React.FC<ListSkeletonProps> = ({
  items = 5,
  showAvatar = false,
  lines = 2
}) => (
  <div className="space-y-4">
    {Array.from({ length: items }, (_, index) => (
      <div key={index} className="flex items-start space-x-3 animate-pulse">
        {showAvatar && (
          <Skeleton width={48} height={48} rounded className="bg-secondary-300 flex-shrink-0" />
        )}
        <div className="flex-1 space-y-2">
          {Array.from({ length: lines }, (_, lineIndex) => (
            <Skeleton
              key={lineIndex}
              width={lineIndex === 0 ? "80%" : "60%"}
              height={lineIndex === 0 ? 16 : 14}
              className={lineIndex === 0 ? "bg-secondary-300" : "bg-secondary-200"}
            />
          ))}
        </div>
      </div>
    ))}
  </div>
);

// Activity Skeleton for Dashboard — matches recent-activities card layout
export const ActivitySkeleton: React.FC = () => (
  <div className="bg-white rounded-xl shadow-sm border border-secondary-100 overflow-hidden">
    <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-200 bg-gradient-to-r from-secondary-50/50 to-white">
      <Skeleton width="45%" height={18} className="bg-secondary-300" />
      <Skeleton width={60} height={18} className="bg-secondary-200 rounded-lg" />
    </div>
    <div className="px-4 py-2 space-y-0 divide-y divide-secondary-100">
      {Array.from({ length: 6 }, (_, index) => (
        <div key={index} className="flex items-start gap-3 py-3 animate-pulse">
          <div className="w-7 h-7 rounded-full bg-secondary-200 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <Skeleton width="80%" height={14} className="bg-secondary-300 mb-1.5" />
            <Skeleton width="55%" height={12} className="bg-secondary-200" />
          </div>
          <Skeleton width={40} height={12} className="bg-secondary-200 flex-shrink-0" />
        </div>
      ))}
    </div>
  </div>
);

// Grid Skeleton for Cards
interface GridSkeletonProps {
  items?: number;
  columns?: number;
  cardHeight?: number;
}

export const GridSkeleton: React.FC<GridSkeletonProps> = ({
  items = 8,
  columns = 4,
  cardHeight = 200
}) => {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
    5: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5',
    6: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6'
  };

  return (
    <div className={`grid ${gridCols[columns as keyof typeof gridCols] || gridCols[4]} gap-6`}>
      {Array.from({ length: items }, (_, index) => (
        <div key={index} className="bg-white rounded-lg shadow-sm p-4 animate-pulse">
          <Skeleton width="100%" height={cardHeight} className="bg-secondary-200" />
        </div>
      ))}
    </div>
  );
};

// Page Skeleton Layout
export const PageSkeleton: React.FC = () => (
  <div className="max-w-7xl mx-auto">
    {/* Header Skeleton */}
    <div className="flex justify-between items-center mb-6">
      <div>
        <Skeleton width={200} height={32} className="bg-secondary-300 mb-2" />
        <Skeleton width={300} height={16} className="bg-secondary-200" />
      </div>
      <Skeleton width={120} height={40} className="bg-secondary-300" />
    </div>

    {/* Stats Cards Skeleton */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
      {Array.from({ length: 4 }, (_, index) => (
        <StatCardSkeleton key={index} />
      ))}
    </div>

    {/* Charts Skeleton */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      <ChartSkeleton />
      <ChartSkeleton />
    </div>

    {/* Tables Skeleton */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <TableSkeleton rows={5} columns={3} />
      <TableSkeleton rows={5} columns={3} />
    </div>
  </div>
);

export default Skeleton;